import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Mail, MessageSquare, Send, CheckCircle2 } from "lucide-react";
import { API } from "../lib/api";

/**
 * Public contact / enquiry form. POST /api/contact.
 *
 *  - Honeypot field named "website" is visually hidden — bots love those and
 *    the backend silently 200s on populated ones.
 *  - Basic client-side validation only; the backend re-validates everything.
 *  - On success we swap the form for a friendly confirmation state so the
 *    user gets clear feedback without a redirect.
 */
const SUBJECTS = [
  { value: "general", label: "General question" },
  { value: "sales", label: "Sales enquiry" },
  { value: "support", label: "Support / bug report" },
  { value: "partnership", label: "Partnership / referral" },
  { value: "refund", label: "Refund request" },
  { value: "press", label: "Press / media" },
  { value: "feedback", label: "Feedback / suggestion" },
];

const ContactPage = () => {
  const [form, setForm] = useState({
    name: "", email: "", subject: "general", message: "", website: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.name.trim().length < 1) return setError("Please tell us your name.");
    if (form.message.trim().length < 10) return setError("Please write a bit more (at least 10 characters) so we can help.");
    setBusy(true);
    try {
      await axios.post(`${API}/contact`, form);
      setDone(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(detail || "Something went wrong. Please try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <section className="min-h-[70vh] bg-cream-100 flex items-center justify-center px-5 py-16">
        <div
          data-testid="contact-success-panel"
          className="max-w-lg w-full bg-white rounded-2xl soft-shadow p-8 md:p-10 text-center"
        >
          <div className="w-14 h-14 rounded-full bg-green-100 text-green-600 flex items-center justify-center mx-auto">
            <CheckCircle2 className="w-8 h-8" />
          </div>
          <h1 className="font-serif italic text-3xl text-navy-700 mt-5">Thank you</h1>
          <p className="mt-3 text-navy-700/70">
            We got your message and a human will reply within 24 hours. A copy has been sent to <strong>{form.email}</strong>.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link
              to="/"
              data-testid="contact-back-home"
              className="inline-flex items-center gap-2 border border-navy-700/20 text-navy-700 px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-cream-100"
            >
              Back home
            </Link>
            <button
              onClick={() => { setDone(false); setForm({ name:"", email:"", subject:"general", message:"", website:"" }); }}
              data-testid="contact-send-another"
              className="inline-flex items-center gap-2 bg-coral-300 text-white px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-coral-400"
            >
              Send another
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="min-h-[70vh] bg-cream-100 pt-20 pb-16">
      <div className="max-w-3xl mx-auto px-5">
        <div className="text-center">
          <p className="tracking-widest text-coral-300 text-xs font-semibold uppercase">Contact</p>
          <h1 className="font-serif text-navy-700 text-5xl md:text-6xl mt-3">
            Say <span className="italic">hello</span>
          </h1>
          <p className="mt-4 text-navy-700/70 max-w-xl mx-auto">
            Questions about pricing, event setup, refunds, or a partnership? Drop us a note — a human replies within 24 hours.
          </p>
        </div>

        <div className="mt-10 grid md:grid-cols-3 gap-6">
          {/* Quick-info column */}
          <div className="md:col-span-1 space-y-4">
            <div className="bg-white rounded-2xl p-5 soft-shadow">
              <div className="flex items-center gap-2 text-navy-700 font-semibold">
                <Mail className="w-4 h-4 text-coral-400" /> Prefer email?
              </div>
              <a
                href="mailto:support@galeriecop.com"
                className="mt-2 block text-sm text-coral-500 hover:underline break-all"
                data-testid="contact-email-link"
              >
                support@galeriecop.com
              </a>
            </div>
            <div className="bg-white rounded-2xl p-5 soft-shadow">
              <div className="flex items-center gap-2 text-navy-700 font-semibold">
                <MessageSquare className="w-4 h-4 text-coral-400" /> Faster help
              </div>
              <p className="mt-2 text-sm text-navy-700/70">
                Ask <strong>Coco</strong>, our AI helper (bottom-right of any page) for instant answers on pricing, uploads, and setup.
              </p>
            </div>
          </div>

          {/* Form column */}
          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="md:col-span-2 bg-white rounded-2xl soft-shadow p-6 md:p-8 space-y-4"
          >
            {/* Honeypot — hidden from humans, catnip for bots */}
            <label className="hidden" aria-hidden="true">
              Website
              <input
                type="text"
                value={form.website}
                onChange={(e) => update("website", e.target.value)}
                autoComplete="off"
                tabIndex={-1}
              />
            </label>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-navy-700/60 mb-1">Your name</label>
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  data-testid="contact-name-input"
                  className="w-full text-sm px-3 py-2.5 rounded-md border border-cream-300 focus:outline-none focus:border-coral-300"
                  maxLength={120}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-navy-700/60 mb-1">Email</label>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  data-testid="contact-email-input"
                  className="w-full text-sm px-3 py-2.5 rounded-md border border-cream-300 focus:outline-none focus:border-coral-300"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-navy-700/60 mb-1">Subject</label>
              <select
                value={form.subject}
                onChange={(e) => update("subject", e.target.value)}
                data-testid="contact-subject-select"
                className="w-full text-sm px-3 py-2.5 rounded-md border border-cream-300 focus:outline-none focus:border-coral-300 bg-white"
              >
                {SUBJECTS.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-navy-700/60 mb-1">Message</label>
              <textarea
                required
                rows={6}
                minLength={10}
                maxLength={4000}
                value={form.message}
                onChange={(e) => update("message", e.target.value)}
                data-testid="contact-message-input"
                placeholder="Tell us as much as you can — event date, tier you're eyeing, what you're stuck on…"
                className="w-full text-sm px-3 py-2.5 rounded-md border border-cream-300 focus:outline-none focus:border-coral-300 resize-y"
              />
              <div className="mt-1 text-[11px] text-navy-700/50 text-right">{form.message.length}/4000</div>
            </div>

            {error && (
              <div
                data-testid="contact-error"
                className="text-sm rounded-md px-3 py-2 bg-red-50 text-red-600"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              data-testid="contact-submit-btn"
              className="w-full inline-flex justify-center items-center gap-2 bg-coral-300 hover:bg-coral-400 text-white font-semibold py-3 rounded-full disabled:opacity-60"
            >
              <Send className="w-4 h-4" />
              {busy ? "Sending…" : "Send message"}
            </button>
            <p className="text-[11px] text-navy-700/50 text-center">
              By submitting you agree to our <Link to="/privacy" className="underline">Privacy Policy</Link>.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
};

export default ContactPage;
